import React, { useState, useEffect, useCallback, useMemo } from "react";
// Sectionized form imports only; internal field inputs moved into sections
import ImageUploadModal from "./ImageUploadModal";
import AddLicenseModal from "./modals/AddLicenseModal";
import AddTrainingModal from "./modals/AddTrainingModal";
import AddEducationModal from "./modals/AddEducationModal";
import AddLanguageModal from "./modals/AddLanguageModal";
import { userService } from "../../services/api";
import ChangePasswordModal from "../common/ChangePasswordModal";
import MobileNumberUpdateModal from "../common/MobileNumberUpdateModal";
// calendar_p is now used in PersonalityTestSection
import ClubDetailsSection from "./sections/ClubDetailsSection";
import WorkPermissionsAvailabilitySection from "./sections/WorkPermissionsAvailabilitySection";
import TechnicalSkillsSection from "./sections/TechnicalSkillsSection";
import LicensesTrainingsSection from "./sections/LicensesTrainingsSection";
import EducationLanguagesSection from "./sections/EducationLanguagesSection";
// The "PERSONALITY TEST . COMING SOON" placeholder this slot used to render
// (sections/PersonalityTestSection.js) is what the Coaching Personality section
// replaces, per Konzept 4.1. The placeholder file is left where it is, unused.
import CoachingPersonalitySection from "./coaching-personality/CoachingPersonalitySection";
import CoachingPhilosophySection from "./sections/CoachingPhilosophySection";
import { isCoachingRole } from "../../constants/staffRoles";

const StaffProfileForm = ({
  isEditing,
  isSaving,
  onEdit,
  onSave,
  onCancel,
  formData: externalFormData,
  setFormData: setExternalFormData,
  disableAutoSave = false,
  // Per-field save errors from the API (e.g. an implausible date of birth) so
  // the message lands on the field that caused it, not only in a banner.
  fieldErrors = {},
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  // Default empty data structure
  const defaultFormData = useMemo(
    () => ({
      account_id: "",
      // Club Details
      firstName: "",
      lastName: "",
      position: "", // This will be read-only
      nationality: "",
      birthday: "",
      height: "",
      weight: "",
      email: "",
      phone: "",
      website: "",
      linkedin: "",
      social: "",
      profileImage: null,

      // Work Permissions & Availability
      passportHeld: [],
      workPermits: [],
      contractStatus: "",
      currentClub: "",
      contractExpiryDate: "",
      willingnessToRelocate: "",
      preferredRegions: [],

      // Technical Skills & Specializations
      videoAnalysis: [],
      gpsPerformance: [],
      software: [],
      specializations: [],

      // Position-specific fields
      coachingPhilosophy: "",
      // `playing_styles` / `defensive_tactics` are stored on the staff record
      // and shown on the public profile, but this form has no input for them.
      // They are still carried in the form state: the save routine reads them,
      // and a value it never loaded is a value it writes back empty.
      // (`offensive_tactics` is deliberately absent — the staff record has no
      // such field; that one exists on the coach only.)
      playingStyles: [],
      defensiveTactics: [],
      managementResponsibilities: [],
      areasOfResponsibility: [],
      toolsUsed: [],
      areaOfSpecialization: "",
      sampleNutritionPlans: null,

      // Licenses & Certifications
      licenses: [],

      // Further Trainings / Workshops
      trainings: [],

      // Education / University Degrees
      education: [],

      // Languages
      languages: [],

      // API fields
      mobile: "",
      staff_type: "",
      role_title_custom: "",
      works_for_club: false,
      club_id: null,
      additional_qualifications: "",
      additional_roles: "",
      has_license_number: false,
      license_organization_name: null,
      license_number: null,
      address: null,
      city: null,
      zipcode: null,
      cvResume: null,
      claim_representation: false,
      sportmonks_coach: null,
      // Removed fields that should NOT be sent in PUT according to MD:
      // email, mobile, website, linkedin, social - handled in account/contact settings
      // passport_held, work_permits, contract_status, current_club, etc. - handled elsewhere
      // role_type - removed as it's not editable
    }),
    []
  );

  // Use external formData if provided, otherwise use local state
  const [localFormData, setLocalFormData] = useState(defaultFormData);
  const formData = externalFormData || localFormData;
  const setFormData = setExternalFormData || setLocalFormData;

  // Fetch staff profile data and documents on component mount
  useEffect(() => {
    const fetchStaffProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [profileResponse, documentsResponse] = await Promise.all([
          userService.getStaffProfile(),
          userService.getStaffDocuments(),
        ]);

        if (profileResponse.success && profileResponse.data) {
          const apiData = profileResponse.data;
          console.log("Staff profile data:", apiData);

          // Map API data to form fields
          const mappedData = {
            ...defaultFormData,
            account_id: apiData.account_id || "",
            // Basic Info
            firstName: apiData.first_name || "",
            lastName: apiData.last_name || "",
            height: apiData.height ? `${apiData.height}` : "",
            weight: apiData.weight ? `${apiData.weight}` : "",
            nationality: apiData.nationality || "",
            birthday: apiData.date_of_birth || "",
            profileImage: apiData.profile_picture_url || null,
            email: apiData.email || "",
            mobile: apiData.mobile || "",
            website: apiData.website_url || "",
            linkedin: apiData.linkedin_url || "",
            social: Array.isArray(apiData.social_urls) && apiData.social_urls.length > 0 
              ? apiData.social_urls[0] 
              : "",

            // Staff specific
            staff_type: apiData.staff_type || "",
            position: apiData.role_type || "", // Read-only field
            role_title_custom: apiData.role_title_custom || "",
            works_for_club: apiData.works_for_club || false,
            sportmonks_coach: apiData.sportmonks_coach || null,
            claim_representation: apiData.claim_representation || false,

            // Work details
            currentClub: apiData.current_club || "",
            contractStatus: apiData.contract_status || "",
            contractExpiryDate: apiData.contract_expiry_date || "",
            address: apiData.address || "",
            city: apiData.city || "",
            country: apiData.country || "",
            zipcode: apiData.zipcode || "",

            // Work permissions (updated to match new API field names)
            passportHeld: Array.isArray(apiData.passports_held)
              ? apiData.passports_held
              : [],
            workPermits: Array.isArray(apiData.work_permits)
              ? apiData.work_permits
              : [],
            contractStatus: apiData.contract_status || "",
            currentClub: apiData.current_organization || "",
            contractExpiryDate: apiData.contract_expiry_date || "",
            willingnessToRelocate: apiData.willingness_to_relocate ? "yes" : "no",
            preferredRegions: Array.isArray(apiData.preferred_regions)
              ? apiData.preferred_regions
              : [],

            // Position-specific fields
            coachingPhilosophy: apiData.coaching_notes || "",
            // Held, not shown. Nothing here edits them, so they leave exactly
            // as they arrived — instead of leaving as an empty list.
            playingStyles: Array.isArray(apiData.playing_styles)
              ? apiData.playing_styles
              : [],
            defensiveTactics: Array.isArray(apiData.defensive_tactics)
              ? apiData.defensive_tactics
              : [],
            managementResponsibilities: Array.isArray(
              apiData.management_responsibilities
            )
              ? apiData.management_responsibilities
              : [],
            areasOfResponsibility: Array.isArray(
              apiData.areas_of_responsibility
            )
              ? apiData.areas_of_responsibility
              : [],
            toolsUsed: Array.isArray(apiData.tools_used)
              ? apiData.tools_used
              : [],
            areaOfSpecialization: apiData.area_of_specialization || "",

            // Technical Skills (updated to match new API field names)
            videoAnalysis: Array.isArray(apiData.technical_skills_video_analysis)
              ? apiData.technical_skills_video_analysis
              : [],
            gpsPerformance: Array.isArray(apiData.technical_skills_gps_performance)
              ? apiData.technical_skills_gps_performance
              : [],
            software: Array.isArray(apiData.technical_skills_software) 
              ? apiData.technical_skills_software 
              : [],
            specializations: Array.isArray(apiData.specializations)
              ? apiData.specializations
              : [],

            // Languages
            languages: Array.isArray(apiData.languages)
              ? apiData.languages.map((lang) => ({
                  id: Date.now() + Math.random(),
                  language: lang,
                  proficiency: "b2", // Default proficiency
                }))
              : [],

            // Career data
            experience: Array.isArray(apiData.experience)
              ? apiData.experience.map((exp) => ({
                  id: Date.now() + Math.random(),
                  club: exp.club,
                  position: exp.position,
                  from: exp.start,
                  to: exp.end,
                  country: exp.country,
                  type: exp.type,
                }))
              : [],

            // Achievements (using trophies field from API)
            achievements: Array.isArray(apiData.trophies)
              ? apiData.trophies.map((trophy) => ({
                  id: Date.now() + Math.random(),
                  title: trophy.title,
                  club: trophy.club,
                  year: trophy.season,
                }))
              : [],

            // Honors
            honors: Array.isArray(apiData.honors_and_awards)
              ? apiData.honors_and_awards.map((award) => ({
                  id: Date.now() + Math.random(),
                  title: award.title,
                  year: award.year,
                  issuer: award.issuer,
                }))
              : [],

            // Contributions
            contributions: Array.isArray(apiData.notable_contributions)
              ? apiData.notable_contributions.map((contrib) => ({
                  id: Date.now() + Math.random(),
                  text: contrib,
                }))
              : [],

            // Publications
            publications: Array.isArray(apiData.publications)
              ? apiData.publications.map((pub) => ({
                  id: Date.now() + Math.random(),
                  title: pub.title,
                  link: pub.link,
                }))
              : [],

            // References
            references: Array.isArray(apiData.references)
              ? apiData.references.map((ref) => ({
                  id: Date.now() + Math.random(),
                  name: ref.name,
                  phone: ref.phone_number,
                  email: ref.email,
                  note: ref.note,
                  isVisible: apiData.reference_contacts_public || false,
                }))
              : [],

            // Licenses (combine regular licenses and gk_licenses)
            licenses: [
              // Regular licenses
              ...(Array.isArray(apiData.licenses)
                ? apiData.licenses.map((license) => ({
                    id: Date.now() + Math.random(),
                    type: license.title,
                    issueDate: license.year,
                    licenseId: license.authority || "",
                    document: null,
                  }))
                : []),
              // Goalkeeping licenses (from gk_licenses field)
              ...(Array.isArray(apiData.gk_licenses)
                ? apiData.gk_licenses.map((gkLicense) => ({
                    id: Date.now() + Math.random(),
                    type: gkLicense,
                    issueDate: new Date().getFullYear(), // Default year since gk_licenses only has title
                    licenseId: "",
                    document: null,
                  }))
                : []),
            ],
          };

          setFormData(mappedData);
        } else {
          throw new Error(profileResponse.error || "Failed to fetch profile");
        }

        // Handle documents
        if (documentsResponse.success && documentsResponse.data) {
          setDocuments(documentsResponse.data);
        }
      } catch (error) {
        console.error("Error fetching staff profile:", error);
        setError(error.message || "Failed to load profile data");
        // Set default data on error
        setFormData(defaultFormData);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStaffProfile();
  }, [defaultFormData, setFormData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear any previous errors when user starts editing
    if (error) {
      setError(null);
    }
  };

  const handleMobileUpdateSuccess = (newMobile) => {
    setFormData((prev) => ({
      ...prev,
      mobile: newMobile,
      phone: newMobile,
    }));
  };



  // Handle cancel by reverting form data to initial state
  const handleCancelInternal = useCallback(() => {
    setError(null);
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  // Document management functions
  const handleDocumentUpload = useCallback(async (docType, title, file) => {
    setIsLoadingDocuments(true);
    try {
      // Create form data for file upload
      const formData = new FormData();
      console.log(file);
      formData.append("file", file);
      formData.append("doc_type", docType);
      formData.append("title", title);
      formData.append("is_primary", docType === "profile_photo");
      formData.append("s3_key", file.name);

      const response = await userService.createStaffDocument(formData);

      if (response.success) {
        // Refresh documents list
        const documentsResponse = await userService.getStaffDocuments();
        if (documentsResponse.success) {
          setDocuments(documentsResponse.data);
        }
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      setError(`Failed to upload ${title}: ${error.message}`);
    } finally {
      setIsLoadingDocuments(false);
    }
  }, []);

  const handleDocumentDelete = useCallback(async (documentId) => {
    setIsLoadingDocuments(true);
    try {
      const response = await userService.deleteStaffDocument(documentId);
      if (response.success) {
        // Remove from local state
        setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      setError(`Failed to delete document: ${error.message}`);
    } finally {
      setIsLoadingDocuments(false);
    }
  }, []);

  // Position options - centralized in constants/staffRoles.js so new titles
  // appear here automatically.
  // eslint-disable-next-line global-require
  const { STAFF_ROLE_OPTIONS } = require("../../constants/staffRoles");
  const positionOptions = STAFF_ROLE_OPTIONS;

  const nationalityOptions = [
    { value: "france", label: "France" },
    { value: "spain", label: "Spain" },
    { value: "germany", label: "Germany" },
    { value: "italy", label: "Italy" },
    { value: "england", label: "England" },
    { value: "portugal", label: "Portugal" },
    { value: "netherlands", label: "Netherlands" },
    { value: "belgium", label: "Belgium" },
    { value: "brazil", label: "Brazil" },
    { value: "argentina", label: "Argentina" },
  ];

  const fifaCountries = [
    { value: "france", label: "France" },
    { value: "spain", label: "Spain" },
    { value: "germany", label: "Germany" },
    { value: "italy", label: "Italy" },
    { value: "england", label: "England" },
    { value: "portugal", label: "Portugal" },
    { value: "netherlands", label: "Netherlands" },
    { value: "belgium", label: "Belgium" },
    { value: "brazil", label: "Brazil" },
    { value: "argentina", label: "Argentina" },
    { value: "uruguay", label: "Uruguay" },
    { value: "chile", label: "Chile" },
    { value: "colombia", label: "Colombia" },
    { value: "mexico", label: "Mexico" },
    { value: "usa", label: "United States" },
    { value: "canada", label: "Canada" },
    { value: "japan", label: "Japan" },
    { value: "south_korea", label: "South Korea" },
    { value: "australia", label: "Australia" },
    { value: "morocco", label: "Morocco" },
    { value: "senegal", label: "Senegal" },
    { value: "nigeria", label: "Nigeria" },
    { value: "ghana", label: "Ghana" },
    { value: "cameroon", label: "Cameroon" },
    { value: "ivory_coast", label: "Ivory Coast" },
    { value: "egypt", label: "Egypt" },
    { value: "tunisia", label: "Tunisia" },
    { value: "algeria", label: "Algeria" },
  ];

  const contractStatusOptions = [
    { value: "employed", label: "Employed" },
    { value: "free_agent", label: "Free Agent" },
    { value: "available_from", label: "Available from [date]" },
  ];

  const clubOptions = [
    { value: "fc_barcelona", label: "FC Barcelona" },
    { value: "real_madrid", label: "Real Madrid" },
    { value: "chelsea", label: "Chelsea" },
    { value: "manchester_city", label: "Manchester City" },
    { value: "manchester_united", label: "Manchester United" },
    { value: "liverpool", label: "Liverpool" },
    { value: "arsenal", label: "Arsenal" },
    { value: "bayern_munich", label: "Bayern Munich" },
    { value: "borussia_dortmund", label: "Borussia Dortmund" },
    { value: "psg", label: "Paris Saint-Germain" },
    { value: "juventus", label: "Juventus" },
    { value: "ac_milan", label: "AC Milan" },
    { value: "inter_milan", label: "Inter Milan" },
    { value: "napoli", label: "Napoli" },
    { value: "roma", label: "Roma" },
    { value: "lazio", label: "Lazio" },
    { value: "atalanta", label: "Atalanta" },
    { value: "fiorentina", label: "Fiorentina" },
    { value: "torino", label: "Torino" },
    { value: "sassuolo", label: "Sassuolo" },
  ];

  const regionOptions = [
    { value: "europe", label: "Europe" },
    { value: "north_america", label: "North America" },
    { value: "south_america", label: "South America" },
    { value: "asia", label: "Asia" },
    { value: "africa", label: "Africa" },
    { value: "oceania", label: "Oceania" },
    { value: "portugal", label: "Portugal" },
    { value: "switzerland", label: "Switzerland" },
    { value: "germany", label: "Germany" },
    { value: "france", label: "France" },
    { value: "spain", label: "Spain" },
    { value: "italy", label: "Italy" },
    { value: "england", label: "England" },
    { value: "netherlands", label: "Netherlands" },
    { value: "belgium", label: "Belgium" },
    { value: "austria", label: "Austria" },
    { value: "denmark", label: "Denmark" },
    { value: "sweden", label: "Sweden" },
    { value: "norway", label: "Norway" },
    { value: "finland", label: "Finland" },
  ];

  const willingnessOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  // Technical Skills Options
  const videoAnalysisOptions = [
    { value: "wyscout", label: "Wyscout" },
    { value: "instat", label: "InStat" },
    { value: "hudl", label: "Hudl" },
    { value: "opta", label: "Opta" },
    { value: "smrtstats", label: "Smrtstats" },
  ];

  const gpsPerformanceOptions = [
    { value: "catapult", label: "Catapult" },
    { value: "statsports", label: "STATSports" },
    { value: "polar", label: "Polar" },
  ];

  const softwareOptions = [
    { value: "excel", label: "Excel" },
    { value: "tableau", label: "Tableau" },
    { value: "r", label: "R" },
    { value: "python", label: "Python" },
    { value: "power_bi", label: "Power BI" },
  ];

  const specializationOptions = [
    { value: "injury_prevention", label: "Injury Prevention" },
    { value: "rehab", label: "Rehab" },
    { value: "data_analysis", label: "Data Analysis" },
    { value: "squad_planning", label: "Squad Planning" },
    { value: "youth_development", label: "Youth Development" },
    { value: "strategy", label: "Strategy" },
    { value: "mental_resilience", label: "Mental Resilience" },
    { value: "nutrition_plans", label: "Nutrition Plans" },
  ];

  // Strength & Conditioning Coach Specializations
  const strengthConditioningOptions = [
    { value: "speed", label: "Speed" },
    { value: "strength", label: "Strength" },
    { value: "recovery", label: "Recovery" },
    { value: "injury_prevention", label: "Injury Prevention" },
    { value: "rehab", label: "Rehab" },
  ];

  // Management Responsibilities for Technical Director
  const managementResponsibilitiesOptions = [
    { value: "squad_planning", label: "Squad Planning" },
    { value: "transfers", label: "Transfers" },
    { value: "academy_development", label: "Academy Development" },
    { value: "strategy", label: "Strategy" },
    { value: "infrastructure", label: "Infrastructure" },
  ];

  // Areas of Responsibility for Sporting Director
  const areasOfResponsibilityOptions = [
    { value: "contracts", label: "Contracts" },
    { value: "transfers", label: "Transfers" },
    { value: "scouting", label: "Scouting" },
    { value: "budget_planning", label: "Budget Planning" },
    { value: "club_development", label: "Club Development" },
  ];

  // Tools Used for Scout/Youth Scout and Performance Analyst
  const toolsUsedOptions = [
    { value: "wyscout", label: "Wyscout" },
    { value: "instat", label: "InStat" },
    { value: "hudl", label: "Hudl" },
    { value: "opta", label: "Opta" },
    { value: "smrtstats", label: "Smrtstats" },
    { value: "other", label: "Other" },
  ];

  // Area of Specialization for Medical Staff
  const areaOfSpecializationOptions = [
    { value: "orthopedics", label: "Orthopedics" },
    { value: "rehabilitation", label: "Rehabilitation" },
    { value: "recovery", label: "Recovery" },
    { value: "sports_medicine", label: "Sports Medicine" },
    { value: "other", label: "Other" },
  ];

  // License Options by Category
  const licenseOptions = [
    // Assistant Coaches
    { value: "uefa_a", label: "UEFA A", category: "assistant_coaches" },
    { value: "uefa_b", label: "UEFA B", category: "assistant_coaches" },
    { value: "uefa_c", label: "UEFA C", category: "assistant_coaches" },
    {
      value: "national_fa",
      label: "National FA licenses",
      category: "assistant_coaches",
    },

    // Goalkeeper Coaches
    {
      value: "uefa_gk_1",
      label: "UEFA GK Level 1",
      category: "goalkeeper_coaches",
    },
    {
      value: "uefa_gk_2",
      label: "UEFA GK Level 2",
      category: "goalkeeper_coaches",
    },
    {
      value: "uefa_gk_3",
      label: "UEFA GK Level 3",
      category: "goalkeeper_coaches",
    },
    { value: "s_c", label: "S&C", category: "goalkeeper_coaches" },

    // Coaches
    { value: "nsca_cscs", label: "NSCA-CSCS", category: "coaches" },
    { value: "uefa_fitness", label: "UEFA Fitness", category: "coaches" },
    {
      value: "fifa_diploma",
      label: "FIFA Diploma in Football Medicine",
      category: "coaches",
    },

    // Medical Staff
    {
      value: "medical_doctor",
      label: "Medical Doctor",
      category: "medical_staff",
    },
    {
      value: "physiotherapist",
      label: "Physiotherapist",
      category: "medical_staff",
    },
    {
      value: "msc_sports_medicine",
      label: "MSc Sports Medicine",
      category: "medical_staff",
    },
    {
      value: "rehab_specialist",
      label: "Rehab Specialist",
      category: "medical_staff",
    },

    // Psychologists
    {
      value: "certified_sports_psychologist",
      label: "Certified Sports Psychologist",
      category: "psychologists",
    },
    { value: "msc", label: "MSc", category: "psychologists" },
    { value: "phd", label: "PhD", category: "psychologists" },

    // Nutritionists
    {
      value: "sports_nutritionist",
      label: "Sports Nutritionist",
      category: "nutritionists",
    },
    { value: "issn", label: "ISSN", category: "nutritionists" },
    {
      value: "msc_nutrition",
      label: "MSc Nutrition",
      category: "nutritionists",
    },

    // Analysts
    { value: "wyscout", label: "Wyscout", category: "analysts" },
    { value: "instat", label: "InStat", category: "analysts" },
    { value: "hudl", label: "Hudl", category: "analysts" },
    { value: "opta", label: "Opta", category: "analysts" },

    // Other
    { value: "other", label: "Other", category: "other" },
  ];

  // Helper functions for licenses and trainings
  const handleAddLicense = (licenseData) => {
    const newLicense = {
      id: Date.now(),
      type: licenseData.type,
      issueDate: licenseData.issueDate,
      licenseId: licenseData.licenseId,
      document: licenseData.document,
    };
    setFormData((prev) => ({
      ...prev,
      licenses: [...prev.licenses, newLicense],
    }));
    setShowLicenseModal(false);
  };

  const handleAddTraining = (trainingData) => {
    const newTraining = {
      id: Date.now(),
      title: trainingData.title,
      document: trainingData.document,
    };
    setFormData((prev) => ({
      ...prev,
      trainings: [...prev.trainings, newTraining],
    }));
    setShowTrainingModal(false);
  };

  // delete handlers not shown in UI for now

  // delete handlers not shown in UI for now

  // Education Options
  const universityOptions = [
    { value: "xyz_university", label: "XYZ University" },
    { value: "harvard", label: "Harvard University" },
    { value: "oxford", label: "University of Oxford" },
    { value: "cambridge", label: "University of Cambridge" },
    { value: "mit", label: "Massachusetts Institute of Technology" },
    { value: "stanford", label: "Stanford University" },
    { value: "ucl", label: "University College London" },
    { value: "imperial", label: "Imperial College London" },
    { value: "lse", label: "London School of Economics" },
    { value: "other", label: "Other" },
  ];

  const degreeOptions = [
    { value: "bachelor_sports_science", label: "Bachelor's in Sports Science" },
    { value: "master_sports_science", label: "Master's in Sports Science" },
    {
      value: "bachelor_physical_education",
      label: "Bachelor's in Physical Education",
    },
    {
      value: "master_physical_education",
      label: "Master's in Physical Education",
    },
    { value: "bachelor_physiotherapy", label: "Bachelor's in Physiotherapy" },
    { value: "master_physiotherapy", label: "Master's in Physiotherapy" },
    { value: "bachelor_medicine", label: "Bachelor's in Medicine" },
    { value: "master_medicine", label: "Master's in Medicine" },
    { value: "phd_sports", label: "PhD in Sports Science" },
    { value: "other", label: "Other" },
  ];

  // Language Options
  const languageOptions = [
    { value: "english", label: "English" },
    { value: "german", label: "German" },
    { value: "french", label: "French" },
    { value: "spanish", label: "Spanish" },
    { value: "portuguese", label: "Portuguese" },
    { value: "arabic", label: "Arabic" },
    { value: "turkish", label: "Turkish" },
    { value: "russian", label: "Russian" },
    { value: "chinese", label: "Chinese" },
    { value: "japanese", label: "Japanese" },
    { value: "italian", label: "Italian" },
    { value: "dutch", label: "Dutch" },
    { value: "swedish", label: "Swedish" },
    { value: "norwegian", label: "Norwegian" },
    { value: "danish", label: "Danish" },
    { value: "finnish", label: "Finnish" },
    { value: "polish", label: "Polish" },
    { value: "czech", label: "Czech" },
    { value: "hungarian", label: "Hungarian" },
    { value: "other", label: "Other" },
  ];

  const proficiencyOptions = [
    { value: "native", label: "Native" },
    { value: "c2", label: "C2 (Proficient)" },
    { value: "c1", label: "C1 (Advanced)" },
    { value: "b2", label: "B2 (Upper Intermediate)" },
    { value: "b1", label: "B1 (Intermediate)" },
    { value: "a2", label: "A2 (Elementary)" },
    { value: "a1", label: "A1 (Beginner)" },
  ];

  // Helper functions for education and languages
  const handleAddEducation = (educationData) => {
    const newEducation = {
      id: Date.now(),
      university: educationData.university,
      degree: educationData.degree,
      startYear: educationData.startYear,
      endYear: educationData.endYear,
      document: educationData.document,
    };
    setFormData((prev) => ({
      ...prev,
      education: [...prev.education, newEducation],
    }));
    setShowEducationModal(false);
  };

  const handleAddLanguage = (languageData) => {
    const newLanguage = {
      id: Date.now(),
      language: languageData.language,
      proficiency: languageData.proficiency,
    };
    setFormData((prev) => ({
      ...prev,
      languages: [...prev.languages, newLanguage],
    }));
    setShowLanguageModal(false);
  };

  // delete handlers not shown in UI for now

  const handleRemoveLanguage = (languageId) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.filter(
        (language) => language.id !== languageId
      ),
    }));
  };

  // Nutrition upload now handled within TechnicalSkillsSection via handleInputChange

  // Show loading state
  if (isLoading) {
    return (
      <div className="px-14 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading profile...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Error Message */}
      {error && (
        <div className="px-14 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="bg-red-100 px-2 py-1.5 rounded-md text-red-800 hover:bg-red-200 text-sm font-medium"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 md:px-14 py-6 space-y-8">
        <ClubDetailsSection
          isEditing={isEditing}
          isSaving={isSaving}
          onEdit={onEdit}
          onCancel={handleCancelInternal}
          onSaveClick={onSave}
          formData={formData}
          handleInputChange={handleInputChange}
          positionOptions={positionOptions}
          nationalityOptions={nationalityOptions}
          onOpenImageUpload={() => setShowImageUpload(true)}
          onOpenChangePassword={() => setShowChangePasswordModal(true)}
          onOpenMobileModal={() => setShowMobileModal(true)}
          dateOfBirthError={
            Array.isArray(fieldErrors.date_of_birth)
              ? fieldErrors.date_of_birth[0]
              : fieldErrors.date_of_birth
          }
        />

        <WorkPermissionsAvailabilitySection
          isEditing={isEditing}
          formData={formData}
          handleInputChange={handleInputChange}
          fifaCountries={fifaCountries}
          contractStatusOptions={contractStatusOptions}
          clubOptions={clubOptions}
          willingnessOptions={willingnessOptions}
          regionOptions={regionOptions}
        />

        {/* Every coaching role has a playing philosophy, not just the assistant
            coach: the head coach, the goalkeeping coach and the strength &
            conditioning coach do too. The canonical role list decides who is a
            coach (staffRoles.js) — a president or a physiotherapist is not. */}
        {isCoachingRole(formData.position) && (
          <CoachingPhilosophySection
            isEditing={isEditing}
            value={formData.coachingPhilosophy}
            onChange={(value) => handleInputChange("coachingPhilosophy", value)}
          />
        )}

        <TechnicalSkillsSection
          isEditing={isEditing}
          formData={formData}
          handleInputChange={handleInputChange}
          videoAnalysisOptions={videoAnalysisOptions}
          gpsPerformanceOptions={gpsPerformanceOptions}
          softwareOptions={softwareOptions}
          strengthConditioningOptions={strengthConditioningOptions}
          managementResponsibilitiesOptions={managementResponsibilitiesOptions}
          areasOfResponsibilityOptions={areasOfResponsibilityOptions}
          toolsUsedOptions={toolsUsedOptions}
          areaOfSpecializationOptions={areaOfSpecializationOptions}
          specializationOptions={specializationOptions}
          onDocumentUpload={handleDocumentUpload}
          documents={documents}
          isLoadingDocuments={isLoadingDocuments}
        />

        <LicensesTrainingsSection
          isEditing={isEditing}
          formData={formData}
          onAddLicense={() => setShowLicenseModal(true)}
          onAddTraining={() => setShowTrainingModal(true)}
          documents={documents}
          onDocumentUpload={handleDocumentUpload}
          onDocumentDelete={handleDocumentDelete}
          isLoadingDocuments={isLoadingDocuments}
        />

        <EducationLanguagesSection
          isEditing={isEditing}
          formData={formData}
          onAddEducation={() => setShowEducationModal(true)}
          onAddLanguage={() => setShowLanguageModal(true)}
          onRemoveLanguage={handleRemoveLanguage}
          proficiencyOptions={proficiencyOptions}
          setFormData={setFormData}
          documents={documents}
          onDocumentUpload={handleDocumentUpload}
          isLoadingDocuments={isLoadingDocuments}
        />

        <CoachingPersonalitySection />
      </div>

      {/* Modals */}
      {showImageUpload && (
        <ImageUploadModal
          onClose={() => setShowImageUpload(false)}
          onUpload={async (imageUrl) => {
            try {
              // Convert blob URL to binary file
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              
              // Create a File object from the blob with proper filename
              const fileName = `profile_picture_${Date.now()}.jpg`;
              const imageFile = new File([blob], fileName, { type: 'image/jpeg' });
              
              // Upload as profile photo document
              await handleDocumentUpload(
                "profile_photo",
                "Profile Picture",
                imageFile
              );
              
              // Set the image URL in form data for display
              handleInputChange("profileImage", imageUrl);
            } catch (error) {
              setError("Failed to upload profile image");
            }
            setShowImageUpload(false);
          }}
        />
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowChangePasswordModal(false)}
          onSuccess={() => {
            // Could add any success handling here
          }}
        />
      )}

      <MobileNumberUpdateModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        initialMobile={formData.mobile || ""}
        onSuccess={handleMobileUpdateSuccess}
      />

      {/* License Modal */}
      {showLicenseModal && (
        <AddLicenseModal
          onClose={() => setShowLicenseModal(false)}
          onAdd={async (licenseData) => {
            // Add to form data
            handleAddLicense(licenseData);

            // Upload document if provided
            if (licenseData.document) {
              try {
                await handleDocumentUpload(
                  "license",
                  licenseData.type,
                  licenseData.document
                );
              } catch (error) {
                setError("License added but document upload failed");
              }
            }
          }}
          licenseOptions={licenseOptions}
        />
      )}

      {/* Training Modal */}
      {showTrainingModal && (
        <AddTrainingModal
          onClose={() => setShowTrainingModal(false)}
          onAdd={async (trainingData) => {
            // Add to form data
            handleAddTraining(trainingData);

            // Upload document if provided
            if (trainingData.document) {
              try {
                await handleDocumentUpload(
                  "training_certificate",
                  trainingData.title,
                  trainingData.document
                );
              } catch (error) {
                setError("Training added but document upload failed");
              }
            }
          }}
        />
      )}

      {/* Education Modal */}
      {showEducationModal && (
        <AddEducationModal
          onClose={() => setShowEducationModal(false)}
          onAdd={async (educationData) => {
            // Add to form data
            handleAddEducation(educationData);

            // Upload document if provided
            if (educationData.document) {
              try {
                await handleDocumentUpload(
                  "degree",
                  `${educationData.degree} - ${educationData.university}`,
                  educationData.document
                );
              } catch (error) {
                setError("Education added but document upload failed");
              }
            }
          }}
          universityOptions={universityOptions}
          degreeOptions={degreeOptions}
        />
      )}

      {/* Language Modal */}
      {showLanguageModal && (
        <AddLanguageModal
          onClose={() => setShowLanguageModal(false)}
          onAdd={handleAddLanguage}
          languageOptions={languageOptions}
          proficiencyOptions={proficiencyOptions}
        />
      )}

      {/* Loading Overlay */}
      {(isSaving || isLoadingDocuments) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-700">
              {isSaving ? "Saving profile..." : "Processing document..."}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default StaffProfileForm;
